import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ACCESS_SERVICES_MAP, APP_SERVICE, MODAL_KEYS, ORDER_METHOD, REQUEST_KINDS } from '../constants';
import { getMaxDistanceSupportShipping, getRestaurantSettings } from '../selectors';
import { calcMoneyFoodList, getPrinterByIndex, getStringFromFood } from '../utils';
import { getCurrentDistanceData, getTotalOrdered, validAmount } from '../utils/order';
import useMoney from './useMoney';
import useModal from './useModal';

import * as actions from '../actions/orderActions';

const regexAskPrinter = /^\(.+\)/;
const regexDefaultPrinter = /^\[.+\]/;

const useOrder = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const {
        ordering: foodsInCart,
        orderMethod,
        deliverPosition,
    } = useSelector(state => state.OrderReducer);
    const { currentcy } = useSelector(getRestaurantSettings);
    const maxDistanceSupport = useSelector(getMaxDistanceSupportShipping);
    const { toggleModal } = useModal(MODAL_KEYS.tableQrcodePrinter);

    const foodsOrdering = foodsInCart.filter(el => !el.submitted);
    const foodsOrdered = foodsInCart.filter(el => el.submitted);
    const currentDistance = (deliverPosition?.distance || 0) / 1000;
    const distanceRequireData = getCurrentDistanceData(currentDistance);
    const {
        total: totalOrdering,
        originTotal: originTotalOrdering
    } = useMoney(calcMoneyFoodList, foodsOrdering);

    const { totalOrdered, originTotalOrdered } = getTotalOrdered(foodsOrdered);
    const shipMoney = orderMethod === ORDER_METHOD.DELIVERY && deliverPosition?.distance !== undefined ? distanceRequireData?.ship_money || 0 : 0;

    const getTotal = () => {
        return {
            total: validAmount(totalOrdering + totalOrdered + shipMoney),
            originTotal: validAmount(originTotalOrdering + originTotalOrdered + shipMoney)
        };
    }

    const getValidShippingDistance = () => {
        if (APP_SERVICE !== ACCESS_SERVICES_MAP.WEB || orderMethod !== ORDER_METHOD.DELIVERY) {
            return {
                validShippingDistance: true,
                shippingDistanceMessage: ''
            };
        }

        if (deliverPosition?.distance !== 0 && !deliverPosition?.distance) {
            return {
                validShippingDistance: false,
                shippingDistanceMessage: ''
            };
        }

        if (!distanceRequireData) {
            return {
                validShippingDistance: false,
                shippingDistanceMessage: t('message.mesOutsideShippingSupportArea', { distance: maxDistanceSupport})
            };
        }

        if (distanceRequireData.min_money > totalOrdering) {
            return {
                validShippingDistance: false,
                shippingDistanceMessage: t('message.minTotalMoneyRequire', {
                    minValue: distanceRequireData?.min_money + currentcy,
                })
            };
        }

        return {
            validShippingDistance: true,
            shippingDistanceMessage: ''
        };
    }

    const addToCart = foodData => {
        console.log({ foodData });
        if (APP_SERVICE !== ORDER_METHOD.QR_CODE) {
            return dispatch(actions.addToCart([ foodData ]));
        }

        if (foodsOrdered?.length) {
            dispatch(actions.requestPayment({
                params: { kind: REQUEST_KINDS.continueOrder },
                backgroundMode: true,
                onDone: () => {}
            }));
        }

        if (
            foodsOrdering.find(food => getStringFromFood(food) === getStringFromFood(foodData)) ||
            !foodData?.foodDetails?.printers ||
            foodData?.printerSelected ||
            foodData?.printerIp ||
            foodData?.index !== -1
        ) {
            return dispatch(actions.addToCart([ foodData ]));
        }

        if (regexAskPrinter.test(foodData?.foodDetails?.printers || '')) {
            // dispatch show modal select printer
            toggleModal(true);
            dispatch(actions.setPrinterSelectData(foodData));
            return;
        }

        if (regexDefaultPrinter.test(foodData?.foodDetails?.printers || '')) {
            const [ _, defaultIndex ] = foodData?.foodDetails?.printers.replace(/\[|\]/g, '').split('-');
            const printerInfo = getPrinterByIndex(+defaultIndex);
            foodData.printerSelected = printerInfo;
            foodData.printerIp = printerInfo?.number + "";
        } else {
            foodData.printerIp = foodData?.foodDetails?.printers;
        }

        dispatch(actions.addToCart([ foodData ]));
    }

    return {
        foodsOrdering,
        foodsOrdered,
        totalOrdering,
        originTotalOrdering,
        totalOrdered,
        originTotalOrdered,
        ...getTotal(),
        distanceRequireData,
        currentDistance,
        ...getValidShippingDistance(),
        shipMoney,
        addToCart
    };
};

export default useOrder;